<template>
  <section class="runner" aria-labelledby="runner-title" :aria-busy="saving">
    <template v-if="step">
      <div class="runner-heading">
        <span class="runner-number" aria-hidden="true">{{
          step.position
        }}</span>
        <div>
          <h2 id="runner-title" tabindex="-1">{{ step.title }}</h2>
          <p>
            Paso {{ step.position }} de {{ steps.length }} ·
            {{ requiredSummary }}
          </p>
        </div>
      </div>
      <form novalidate @submit.prevent="submit">
        <div
          v-if="error"
          ref="error"
          class="runner-error"
          role="alert"
          tabindex="-1"
        >
          <v-icon small aria-hidden="true">error_outline</v-icon
          ><span>{{ error }}</span>
        </div>
        <div v-if="step.requiresPhoto || showExtras" class="runner-field">
          <div class="runner-label-row">
            <label for="step-photos">Fotografías</label
            ><span>{{ step.requiresPhoto ? 'Obligatorio' : 'Opcional' }}</span>
          </div>
          <div
            class="runner-upload"
            :class="{
              'runner-upload--drag': dragging,
              'runner-upload--error': fieldErrors.photos,
            }"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="dropFiles"
          >
            <div class="runner-upload-icon">
              <v-icon aria-hidden="true">add_a_photo</v-icon>
            </div>
            <div class="runner-upload-copy">
              <strong>{{
                files.length
                  ? `${files.length} foto${
                      files.length === 1 ? '' : 's'
                    } seleccionada${files.length === 1 ? '' : 's'}`
                  : 'Agrega una foto de este paso'
              }}</strong
              ><span>Hasta 8 fotos · JPG, PNG o WebP · 10 MB por foto</span>
            </div>
            <div class="runner-upload-actions">
              <button
                type="button"
                class="runner-button runner-button--secondary"
                :disabled="saving"
                @click="$refs.camera.click()"
              >
                <v-icon small aria-hidden="true">photo_camera</v-icon>Tomar foto
              </button>
              <button
                type="button"
                class="runner-button runner-button--quiet"
                :disabled="saving"
                @click="$refs.photos.click()"
              >
                Seleccionar archivo
              </button>
            </div>
            <input
              id="step-photos"
              ref="photos"
              class="runner-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              tabindex="-1"
              :disabled="saving"
              :aria-invalid="!!fieldErrors.photos"
              aria-describedby="photo-error"
              @change="chooseFiles"
            />
            <input
              ref="camera"
              class="runner-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              tabindex="-1"
              aria-label="Tomar fotografía"
              :disabled="saving"
              @change="chooseFiles"
            />
          </div>
          <p
            v-if="fieldErrors.photos"
            id="photo-error"
            class="runner-field-error"
          >
            {{ fieldErrors.photos }}
          </p>
          <div v-if="previews.length" class="runner-previews">
            <figure v-for="(src, i) in previews" :key="src">
              <img :src="src" :alt="`Fotografía ${i + 1} seleccionada`" />
              <figcaption>{{ files[i].name }}</figcaption>
              <button
                type="button"
                :aria-label="`Quitar fotografía ${i + 1}`"
                :disabled="saving"
                @click="removeFile(i)"
              >
                <v-icon small aria-hidden="true">close</v-icon>
              </button>
            </figure>
          </div>
        </div>
        <div v-if="step.requiresComment || showExtras" class="runner-field">
          <div class="runner-label-row">
            <label for="step-comment">Comentario</label
            ><span>{{
              step.requiresComment ? 'Obligatorio' : 'Opcional'
            }}</span>
          </div>
          <textarea
            id="step-comment"
            v-model="comment"
            rows="3"
            :required="step.requiresComment"
            :disabled="saving"
            maxlength="10000"
            :aria-invalid="!!fieldErrors.comment"
            aria-describedby="comment-error"
            placeholder="¿Hay algo que debamos saber de este paso?"
            @input="fieldErrors.comment && validateField('comment')"
            @blur="validateField('comment')"
          ></textarea>
          <p
            v-if="fieldErrors.comment"
            id="comment-error"
            class="runner-field-error"
          >
            {{ fieldErrors.comment }}
          </p>
        </div>
        <div v-if="step.requiresReceiverName" class="runner-field">
          <div class="runner-label-row">
            <label for="step-receiver">Nombre y apellido de quien recibe</label
            ><span>Obligatorio</span>
          </div>
          <input
            id="step-receiver"
            v-model="receiver"
            required
            :disabled="saving"
            maxlength="255"
            autocomplete="name"
            :aria-invalid="!!fieldErrors.receiver"
            aria-describedby="receiver-error"
            placeholder="Nombre completo"
            @input="fieldErrors.receiver && validateField('receiver')"
            @blur="validateField('receiver')"
          />
          <p
            v-if="fieldErrors.receiver"
            id="receiver-error"
            class="runner-field-error"
          >
            {{ fieldErrors.receiver }}
          </p>
        </div>
        <div v-if="step.requiresSignature" class="runner-field">
          <div class="runner-label-row">
            <span class="runner-label">Firma de quien recibe</span
            ><span>Obligatorio</span>
          </div>
          <signature-pad
            ref="signature"
            v-model="signature"
            :disabled="saving"
            @input="validateField('signature')"
          />
          <p v-if="fieldErrors.signature" class="runner-field-error">
            {{ fieldErrors.signature }}
          </p>
        </div>
        <div
          v-if="!step.requiresPhoto || !step.requiresComment"
          class="runner-extras"
        >
          <button
            type="button"
            class="runner-extra-toggle"
            :aria-expanded="String(showExtras)"
            :disabled="saving"
            @click="showExtras = !showExtras"
          >
            <v-icon small aria-hidden="true">{{
              showExtras ? 'remove' : 'add'
            }}</v-icon
            >{{ showExtras ? 'Ocultar datos opcionales' : optionalFieldsLabel
            }}<span>Opcional</span>
          </button>
          <p v-if="!showExtras">
            {{
              step.requiresPhoto ||
              step.requiresComment ||
              step.requiresSignature ||
              step.requiresReceiverName
                ? 'Los campos obligatorios se mantienen visibles.'
                : 'Confirma que realizaste este paso para continuar con el servicio.'
            }}
          </p>
        </div>
        <div class="runner-location">
          <v-icon small aria-hidden="true">{{
            coords ? 'location_on' : 'location_off'
          }}</v-icon>
          <p aria-live="polite">{{ locationMessage }}</p>
          <button type="button" :disabled="saving || locating" @click="locate">
            {{
              locating
                ? 'Buscando…'
                : coords
                ? 'Actualizar'
                : 'Agregar ubicación'
            }}
          </button>
        </div>
        <div class="runner-footer">
          <p>
            <v-icon small aria-hidden="true">{{
              allRequiredReady ? 'check_circle_outline' : 'info_outline'
            }}</v-icon
            >{{
              allRequiredReady
                ? 'Todo listo para registrar este paso'
                : 'Completa los campos obligatorios para continuar'
            }}
          </p>
          <button
            type="submit"
            class="runner-button runner-button--primary"
            :disabled="saving"
          >
            <v-progress-circular
              v-if="saving"
              indeterminate
              size="18"
              width="2"
              color="white"
            /><v-icon v-else small aria-hidden="true">check</v-icon
            >{{ saving ? 'Guardando paso…' : 'Completar paso'
            }}<v-icon v-if="!saving" small aria-hidden="true"
              >arrow_forward</v-icon
            >
          </button>
        </div>
      </form>
    </template>
    <div v-else class="runner-complete">
      <span><v-icon size="32" aria-hidden="true">done_all</v-icon></span>
      <h2 id="runner-title" tabindex="-1">Servicio completado</h2>
      <p>
        Todos los pasos quedaron registrados. Puedes consultar las evidencias y
        el historial del servicio.
      </p>
    </div>
  </section>
</template>
<script>
import Api from '@/api'
import SignaturePad from './SignaturePad.vue'
export default {
  components: { SignaturePad },
  props: {
    steps: { type: Array, required: true },
    shipmentId: { type: [Number, String], required: true },
  },
  data: () => ({
    comment: '',
    receiver: '',
    signature: '',
    files: [],
    previews: [],
    saving: false,
    error: '',
    validationError: false,
    coords: null,
    locating: false,
    dragging: false,
    showExtras: false,
    fieldErrors: {},
    locationMessage: 'Ubicación no registrada (opcional).',
  }),
  computed: {
    step() {
      return this.steps.find(s => s.status !== 'completado')
    },
    requiredSummary() {
      if (!this.step) return ''
      const count = [
        this.step.requiresPhoto,
        this.step.requiresComment,
        this.step.requiresSignature,
        this.step.requiresReceiverName,
      ].filter(Boolean).length
      return count
        ? `${count} ${
            count === 1 ? 'requisito obligatorio' : 'requisitos obligatorios'
          }`
        : 'Registra el avance de tu servicio'
    },
    optionalFieldsLabel() {
      if (this.step.requiresPhoto) return 'Agregar comentario'
      if (this.step.requiresComment) return 'Agregar fotografías'
      return 'Agregar foto o comentario'
    },
    allRequiredReady() {
      const s = this.step
      return (
        s &&
        (!s.requiresPhoto || this.files.length) &&
        (!s.requiresComment || this.comment.trim()) &&
        (!s.requiresReceiverName ||
          this.receiver.trim().split(/\s+/).length >= 2) &&
        (!s.requiresSignature || this.signature)
      )
    },
  },
  watch: {
    'step.id'() {
      this.reset()
    },
  },
  beforeDestroy() {
    this.previews.forEach(URL.revokeObjectURL)
  },
  methods: {
    reset() {
      this.comment = ''
      this.receiver = ''
      this.signature = ''
      this.files = []
      this.error = ''
      this.validationError = false
      this.coords = null
      this.fieldErrors = {}
      this.locating = false
      this.showExtras = false
      this.locationMessage = 'Ubicación no registrada (opcional).'
      this.previews.forEach(URL.revokeObjectURL)
      this.previews = []
      if (this.$refs.photos) this.$refs.photos.value = ''
      if (this.$refs.camera) this.$refs.camera.value = ''
      if (this.$refs.signature) this.$refs.signature.clear()
    },
    validateField(field) {
      const s = this.step
      if (!s) return
      const messages = {
        photos:
          s.requiresPhoto && !this.files.length
            ? 'Adjunte al menos una foto.'
            : '',
        comment:
          s.requiresComment && !this.comment.trim()
            ? 'Escriba el comentario obligatorio.'
            : '',
        receiver:
          s.requiresReceiverName && this.receiver.trim().split(/\s+/).length < 2
            ? 'Escriba nombre y apellido de quien recibe.'
            : '',
        signature:
          s.requiresSignature && !this.signature
            ? 'Solicite la firma de quien recibe.'
            : '',
      }
      this.$set(this.fieldErrors, field, messages[field])
      if (this.validationError) {
        this.error = Object.values(this.fieldErrors).find(Boolean) || ''
        if (!this.error) this.validationError = false
      }
    },
    chooseFiles(e) {
      this.addFiles(Array.from(e.target.files || []))
      e.target.value = ''
    },
    dropFiles(e) {
      this.dragging = false
      if (!this.saving) this.addFiles(Array.from(e.dataTransfer.files || []))
    },
    addFiles(incoming) {
      if (
        incoming.some(
          f =>
            f.size > 10 * 1024 * 1024 ||
            !['image/jpeg', 'image/png', 'image/webp'].includes(f.type)
        ) ||
        this.files.length + incoming.length > 8
      ) {
        this.$set(
          this.fieldErrors,
          'photos',
          'Use hasta 8 fotos JPG, PNG o WebP, de máximo 10 MB cada una.'
        )
        return
      }
      this.files.push(...incoming)
      this.previews.push(...incoming.map(f => URL.createObjectURL(f)))
      this.validateField('photos')
    },
    removeFile(index) {
      URL.revokeObjectURL(this.previews[index])
      this.previews.splice(index, 1)
      this.files.splice(index, 1)
      this.validateField('photos')
    },
    locate() {
      if (!navigator.geolocation) {
        this.locationMessage =
          'Ubicación no disponible en este navegador. Puedes continuar.'
        return
      }
      const stepId = this.step && this.step.id
      this.locating = true
      this.locationMessage = 'Buscando tu ubicación…'
      navigator.geolocation.getCurrentPosition(
        p => {
          if (!this.step || stepId !== this.step.id) return
          this.coords = {
            latitude: p.coords.latitude,
            longitude: p.coords.longitude,
          }
          this.locating = false
          this.locationMessage = 'Ubicación lista para registrar con este paso.'
        },
        () => {
          if (!this.step || stepId !== this.step.id) return
          this.locating = false
          this.locationMessage =
            'No pudimos obtener la ubicación. Revisa los permisos o continúa sin ella.'
        },
        { enableHighAccuracy: true, timeout: 10000 }
      )
    },
    async submit() {
      if (this.saving || !this.step) return
      this.error = ''
      this.validationError = false
      ;['photos', 'comment', 'receiver', 'signature'].forEach(
        this.validateField
      )
      const firstError = Object.values(this.fieldErrors).find(Boolean)
      if (firstError) {
        this.error = firstError
        this.validationError = true
        this.$nextTick(() => this.$refs.error.focus())
        return
      }
      const form = new FormData()
      this.files.forEach(f => form.append('file', f))
      form.append('comment', this.comment.trim())
      form.append('receiver_name', this.receiver.trim())
      if (this.signature) form.append('signature_data', this.signature)
      if (this.coords)
        Object.keys(this.coords).forEach(k => form.append(k, this.coords[k]))
      this.saving = true
      try {
        const res = await Api.Shipment.completeStep(
          this.shipmentId,
          this.step.id,
          form
        )
        if (!res || res.status >= 400)
          throw new Error(
            (res && res.data && (res.data.message || res.data.error)) ||
              'No se pudo enviar el paso. Tus datos siguen aquí; reintenta cuando tengas conexión.'
          )
        this.$emit('completed', res.data)
      } catch (e) {
        this.error = e.message
        this.$nextTick(() => this.$refs.error && this.$refs.error.focus())
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
<style scoped>
.runner {
  scroll-margin-top: 88px;
  --runner-blue: #254f87;
  --runner-ink: #182b44;
  --runner-muted: #596a80;
  --runner-line: #e1e7ef;
  color: var(--runner-ink);
  background: #fff;
  border: 1px solid var(--runner-line);
  border-radius: 16px;
  overflow: hidden;
}
.runner-heading {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 28px 28px 24px;
  border-bottom: 1px solid var(--runner-line);
}
.runner-heading h2 {
  font-size: 23px;
  line-height: 1.35;
  margin: 0 0 6px;
  letter-spacing: -0.02em;
  font-weight: 700;
  text-wrap: balance;
}
.runner-heading p {
  color: var(--runner-muted);
  font-size: 14px;
  margin: 0;
}
.runner-number {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 12px;
  color: var(--runner-blue);
  background: #eaf0f9;
  font-size: 21px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.runner form {
  padding: 0 28px;
}
.runner-field {
  margin: 24px 0;
}
.runner-label-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px 12px;
  margin-bottom: 10px;
}
.runner-label-row label,
.runner-label {
  font-weight: 600;
  font-size: 15px;
}
.runner-label-row > span:last-child:not(.runner-label) {
  color: var(--runner-muted);
  font-size: 12px;
}
.runner-upload {
  position: relative;
  border: 1px dashed #b0c1d6;
  border-radius: 12px;
  background: #f8fafd;
  padding: 24px;
  text-align: center;
  transition: border-color 0.18s, background-color 0.18s;
}
.runner-upload--drag {
  border-color: var(--runner-blue);
  background: #edf3fb;
}
.runner-upload--error {
  border-color: #b42333;
}
.runner-upload-icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  margin: 0 auto 12px;
  border-radius: 12px;
  background: #e9eff7;
}
.runner-upload-icon .v-icon {
  color: var(--runner-blue);
}
.runner-upload-copy strong,
.runner-upload-copy span {
  display: block;
}
.runner-upload-copy strong {
  font-size: 15px;
  font-weight: 500;
}
.runner-upload-copy span {
  color: var(--runner-muted);
  font-size: 12px;
  margin-top: 5px;
}
.runner-upload-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}
.runner-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 18px;
  min-height: 44px;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.18s, transform 0.18s;
}
.runner-button:active:not(:disabled) {
  transform: translateY(1px);
}
.runner-button--secondary {
  background: #fff;
  border: 1px solid #b5c3d5;
  color: var(--runner-blue);
}
.runner-button--secondary:hover {
  background: #eef3f9;
}
.runner-button--quiet {
  color: var(--runner-blue);
}
.runner-button--quiet:hover {
  background: #eaf0f8;
}
.runner-button--primary {
  background: var(--runner-blue);
  color: #fff;
  min-height: 48px;
}
.runner-button--primary .v-icon {
  color: inherit;
}
.runner-button--primary:hover {
  background: #1b3d6b;
}
.runner-button:disabled,
.runner-location button:disabled {
  opacity: 0.6;
  cursor: wait;
}
.runner-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
textarea,
input:not([type='file']) {
  display: block;
  width: 100%;
  font: inherit;
  font-size: 16px;
  line-height: 1.5;
  padding: 13px 14px;
  color: var(--runner-ink);
  border: 1px solid #b8c5d5;
  border-radius: 9px;
  background: #fff;
  resize: vertical;
  caret-color: var(--runner-blue);
}
textarea::placeholder,
input::placeholder {
  color: #64748b;
}
[aria-invalid='true']:not([type='file']) {
  border-color: #b42333;
}
.runner-previews {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 14px;
}
.runner-previews figure {
  position: relative;
  width: 100px;
  margin: 0;
}
.runner-previews img {
  display: block;
  width: 100px;
  height: 88px;
  object-fit: cover;
  border-radius: 8px;
}
.runner-previews figcaption {
  font-size: 12px;
  color: var(--runner-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: 5px;
}
.runner-previews button {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  background: #fff;
  border: 1px solid #c7d2e0;
  border-radius: 50%;
}
.runner-previews button .v-icon {
  color: #33465e;
}
.runner-extras {
  margin: 20px 0;
}
.runner-extra-toggle {
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: 44px;
  color: var(--runner-blue);
  font-weight: 600;
  text-align: left;
}
.runner-extra-toggle .v-icon {
  color: inherit;
}
.runner-extra-toggle span {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 400;
  color: var(--runner-muted);
}
.runner-extras > p {
  margin: 0 0 12px;
  color: var(--runner-muted);
  font-size: 13px;
}
.runner-location {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0;
  border-top: 1px solid var(--runner-line);
}
.runner-location > .v-icon {
  color: var(--runner-muted);
}
.runner-location p {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--runner-muted);
  font-size: 12px;
}
.runner-location button {
  min-height: 44px;
  color: var(--runner-blue);
  font-size: 12px;
  font-weight: 600;
}
.runner-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  border-top: 1px solid var(--runner-line);
  padding: 20px 0 24px;
}
.runner-footer p {
  display: flex;
  align-items: center;
  gap: 7px;
  max-width: 230px;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--runner-muted);
}
.runner-footer p .v-icon {
  color: #387650;
}
.runner-error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px;
  margin: 20px 0;
  border: 1px solid #efc4ca;
  border-radius: 8px;
  color: #a01e2d;
  background: #fff6f7;
  font-size: 14px;
}
.runner-error .v-icon {
  color: inherit;
}
.runner-field-error {
  font-size: 13px;
  color: #a01e2d;
  margin: 8px 0 0;
}
.runner-complete {
  padding: 48px 28px;
  text-align: center;
}
.runner-complete > span {
  display: grid;
  place-items: center;
  width: 68px;
  height: 68px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: #edf7f0;
}
.runner-complete .v-icon {
  color: #287044;
}
.runner-complete h2 {
  margin: 0 0 10px;
  font-size: 24px;
}
.runner-complete p {
  max-width: 45ch;
  color: var(--runner-muted);
  margin: auto;
  line-height: 1.6;
}
.runner :focus-visible {
  outline: 3px solid #467db6;
  outline-offset: 3px;
}
@media (max-width: 600px) {
  .runner-heading {
    padding: 20px;
    gap: 12px;
  }
  .runner-heading h2 {
    font-size: 21px;
  }
  .runner-heading p {
    font-size: 12px;
    line-height: 1.5;
  }
  .runner form {
    padding: 0 20px;
  }
  .runner-upload {
    padding: 20px 12px;
  }
  .runner-upload-actions {
    gap: 4px;
  }
  .runner-button {
    padding: 0 12px;
  }
  .runner-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding-bottom: max(20px, env(safe-area-inset-bottom));
  }
  .runner-footer p {
    max-width: none;
    justify-content: center;
  }
  .runner-button--primary {
    width: 100%;
    min-height: 50px;
  }
  .runner-extra-toggle {
    flex-wrap: wrap;
    font-size: 14px;
  }
  .runner-extra-toggle span {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .runner-button,
  .runner-upload {
    transition: none;
  }
}
</style>
